import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { homePathForRole } from "../lib/homePath";
import { CompassLogo } from "../components/CompassLogo";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Loader2, XCircle } from "lucide-react";

/**
 * Issue #510 — the magic-link exchange landing page. The emailed link points
 * here (`/sign-in-link/callback?token=…`); this page reads the token and
 * POSTs it to the exchange endpoint via `completeSignInLink`, then lands the
 * user on their role's home.
 *
 * WHY THE CONSUME IS A POST THIS PAGE MAKES, not the GET the email links to:
 * an email-client or browser link PREFETCH issues a GET, which would burn a
 * single-use token before the human ever clicked. The link is a plain
 * landing GET; the token is only spent by the same-origin POST this page
 * fires on mount, which a prefetch never sends. Same reason the reset flow
 * links to a page rather than a consume endpoint.
 *
 * The exchange is single-use, so it must fire EXACTLY once — the `ran` ref
 * guards against React's development double-mount (a second POST would find
 * the token already consumed and flash a spurious error).
 *
 * D144 / issue #889 — WHY THE EFFECT ALSO WAITS ON `user`. `AuthContext`
 * exposes auth as three states through one field: `null` while the mount
 * probe to `/auth/me` is still in flight, `false` once it resolves to
 * anonymous, and the user object once it resolves signed-in
 * (`ProtectedRoute`'s own `user === null` check is the same idiom). This
 * effect used to key off `token` alone, so it fired the consume POST on
 * every mount regardless of `user` — including a hard load, where `user` is
 * still `null` and the visitor's OWN session has not been read yet, and a
 * client-side navigation into this route while already signed in, where the
 * render-time `<Navigate>` guard below never stops the effect (a hook runs
 * on mount whether or not the JSX it sits above ends up rendered). Either
 * way, a signed-in visitor who opened someone else's link got signed into
 * that account: the token was spent and the session silently became the
 * link's. Waiting for `user !== null` before ever consuming, and refusing
 * to consume at all once `user` is truthy, is what keeps this visitor on
 * their own session and leaves the token unspent for its rightful owner.
 */
export default function SignInLinkCallback() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, completeSignInLink } = useAuth();
  const nav = useNavigate();
  const ran = useRef(false);
  // "working" until the exchange resolves; "error" if the token was
  // invalid/expired/used. Success navigates away, so there is no "success"
  // terminal state to render. Also the state shown while auth itself is
  // still loading (`user === null`) — indistinguishable from the exchange
  // being mid-flight, which is the point: nothing below fires until we know.
  const [state, setState] = useState(token ? "working" : "missing");

  useEffect(() => {
    if (!token || ran.current) return;
    // Auth hasn't resolved yet (hard load) — wait. Re-runs when `user`
    // settles, via the dep below.
    if (user === null) return;
    if (user) {
      // Already signed in, for this account or another's: go home and
      // leave the token unspent (D144). Deliberately does NOT set
      // `ran.current` — that flag means "the exchange fired", and it never
      // does on this branch.
      nav(homePathForRole(user.role), { replace: true });
      return;
    }
    ran.current = true;
    (async () => {
      const signedIn = await completeSignInLink(token);
      if (signedIn) {
        toast.success("Signed in");
        // `replace`, so the one-time callback URL (token in the query
        // string) is not left in history for a Back press to re-hit.
        nav(homePathForRole(signedIn.role), { replace: true });
      } else {
        setState("error");
      }
    })();
    // completeSignInLink/nav are stable enough for this one-shot effect; the
    // ref guard is what actually enforces once, not the dep list. `user` IS
    // a real dependency, unlike those two — see the D144 note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]);

  // Already signed in AND the exchange isn't mid-flight (ref untouched):
  // nothing to do here, send them home. This is what fires FIRST for a
  // client-side navigation into an already-signed-in session — `user` is
  // truthy on the very first render, so the page renders this instead of
  // the "working" UI at all — and it also catches the re-render right after
  // a successful exchange in the same tick as `nav` above. The effect's own
  // `user`-truthy branch is the belt-and-suspenders for the OTHER timing:
  // `user` resolving to truthy asynchronously (a hard load) after the
  // "working" UI already rendered.
  if (user && !ran.current) return <Navigate to={homePathForRole(user.role)} replace />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="sign-in-link-callback-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg px-3 py-2 inline-flex mb-6">
          <CompassLogo size={44} />
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          {state === "working" && (
            <div className="flex flex-col items-center text-center" data-testid="sil-cb-working">
              <Loader2 size={28} className="animate-spin text-slate-500" />
              <h2 className="font-display font-black text-2xl text-slate-900 mt-4">Signing you in…</h2>
              <p className="text-slate-500 mt-1 text-sm">One moment while we verify your link.</p>
            </div>
          )}

          {(state === "error" || state === "missing") && (
            <div className="text-center" data-testid="sil-cb-error">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 mx-auto" style={{ background: "#ef444415" }}>
                <XCircle size={22} color="#ef4444" />
              </div>
              <h2 className="font-display font-black text-2xl text-slate-900">This link didn't work</h2>
              <p className="text-slate-500 mt-2 text-sm">
                {state === "missing"
                  ? "This link is missing its sign-in token."
                  : "Sign-in links expire after a short time and can only be used once. Request a fresh one to continue."}
              </p>
              <div className="mt-6 space-y-2">
                <Link to="/sign-in-link">
                  <Button className="w-full h-11" style={{ background: "#0B2E59" }} data-testid="sil-cb-request-new">
                    Email me a new link
                  </Button>
                </Link>
                <Link to="/login" className="inline-block text-sm text-slate-600 hover:text-slate-900" data-testid="sil-cb-password">
                  Sign in with a password instead
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
